import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LogoUploadSection = ({ logo, onLogoChange, error }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(logo || null);

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e?.dataTransfer?.files);
    if (files?.length > 0) {
      handleFile(files?.[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files);
    if (files?.length > 0) {
      handleFile(files?.[0]);
    }
  };

  const handleFile = (file) => {
    if (!file?.type?.startsWith('image/')) {
      return;
    }

    if (file?.size > 5242880) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader?.result);
      onLogoChange(reader?.result);
    };
    reader?.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreview(null);
    onLogoChange(null);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Image" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Company Logo</h2>
          <p className="text-sm text-text-secondary">Upload your company logo for branding</p>
        </div>
      </div>

      {preview ? (
        <div className="space-y-4">
          <div className="relative bg-muted rounded-lg p-8 flex items-center justify-center">
            <Image
              src={preview}
              alt="Company logo preview showing uploaded branding image"
              className="max-h-32 w-auto object-contain"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemove}
              iconName="Trash2"
              iconPosition="left"
              iconSize={14}
            >
              Remove Logo
            </Button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
              />
              <Button
                variant="secondary"
                size="sm"
                iconName="Upload"
                iconPosition="left"
                iconSize={14}
                asChild
              >
                Change Logo
              </Button>
            </label>
          </div>
        </div>
      ) : (
        <div
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-all duration-150 cursor-pointer
            ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary hover:bg-primary/5'}
            ${error ? 'border-error' : ''}
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <label className="cursor-pointer block">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
            />
            <Icon name="Upload" size={32} className="mx-auto mb-4 text-text-secondary" />
            <p className="text-sm font-medium text-text-primary mb-1">
              Drop logo here or click to browse
            </p>
            <p className="text-xs text-text-secondary">
              PNG, JPG, SVG up to 5MB. Recommended: 200x60px
            </p>
          </label>
        </div>
      )}

      {error && (
        <p className="text-sm text-error mt-2">{error}</p>
      )}
    </div>
  );
};

export default LogoUploadSection;